"use client";

/**
 * The one line every tool activity renders as: an icon, a label that says what
 * the agent is doing or did, and, for rows with something to show, a chevron
 * and a trailing count that open a detail panel underneath. Rows that carry
 * a decision (a memory awaiting approval) put their buttons on the right of
 * the same line. Kept apart from the activity list so surfaces that compose
 * rows of their own (memory) can import it without a cycle.
 */

import type { ToolCallMessagePartStatus } from "@assistant-ui/react";
import { ChevronRightIcon, SparklesIcon, type LucideIcon } from "lucide-react";
import type { FC, ReactNode } from "react";

import { cn } from "@/agents-v2/utils/cn";

export type ToolActivityStatus = "running" | "complete" | "error";

/** What a tool is called in the thread: one label per status, and an icon. */
export type ToolActivity = {
  key: string;
  runningLabel: string;
  completeLabel: string;
  errorLabel: string;
  icon: LucideIcon;
};

/** A tool-call message part as the runtime emits it. */
export type ToolActivityPart = {
  type: "tool-call";
  toolCallId: string;
  toolName: string;
  args?: unknown;
  argsText?: string;
  result?: unknown;
  isError?: boolean;
  status?: ToolCallMessagePartStatus;
};

export const isToolActivityPart = (part: unknown): part is ToolActivityPart =>
  Boolean(part) &&
  typeof part === "object" &&
  (part as { type?: unknown }).type === "tool-call" &&
  typeof (part as { toolName?: unknown }).toolName === "string";

/**
 * One status for a group of parts: running while any runs, error when any
 * failed, complete otherwise.
 * @param parts - The parts of one activity group.
 * @returns The aggregate status.
 */
export const getAggregateStatus = (
  parts: readonly ToolActivityPart[]
): ToolActivityStatus => {
  if (
    parts.some(
      (part) =>
        part.status?.type === "running" ||
        part.status?.type === "requires-action"
    )
  ) {
    return "running";
  }
  if (parts.some((part) => part.isError || part.status?.type === "incomplete")) {
    return "error";
  }
  return "complete";
};

// The vertical margin sits on whichever element is outermost: the line
// itself, or the wrapper that holds it beside its actions. Inside a flex
// wrapper the line's own margin would not collapse with the next row's, and
// the gap under a row with actions would double.
const ROW_CLASS =
  "aui-tool-activity-row flex w-full items-center gap-2 rounded-md px-1 py-1 text-sm text-muted-foreground";

/**
 * The activity line.
 * @param props - Component props.
 * @param props.runningLabel - Label while the tool runs.
 * @param props.completeLabel - Label once it finished.
 * @param props.errorLabel - Label when it failed.
 * @param props.icon - Icon before the label.
 * @param props.status - Which label to show.
 * @param props.open - Whether the detail panel is open (expandable rows).
 * @param props.sourceCount - Retrieval rows: how many sources, shown trailing.
 * @param props.detailLabel - Any other trailing text ("2 memories").
 * @param props.hasDetails - Whether there is anything to expand; defaults to
 *   having sources.
 * @param props.onToggleSources - Makes the row a button that toggles the panel.
 * @param props.actions - Controls on the right of the line, outside the toggle.
 * @returns The row.
 */
export const ToolActivityRow: FC<{
  runningLabel?: string;
  completeLabel?: string;
  errorLabel?: string;
  icon?: LucideIcon;
  status: ToolActivityStatus;
  open?: boolean;
  sourceCount?: number;
  detailLabel?: string;
  hasDetails?: boolean;
  onToggleSources?: () => void;
  actions?: ReactNode;
}> = ({
  runningLabel,
  completeLabel,
  errorLabel,
  icon: Icon = SparklesIcon,
  status,
  open,
  sourceCount,
  detailLabel,
  hasDetails,
  onToggleSources,
  actions
}) => {
  const isRunning = status === "running";
  const isError = status === "error";
  const isExpandable = onToggleSources !== undefined;
  const hasSources = (sourceCount ?? 0) > 0;
  const hasExpandableDetails = hasDetails ?? hasSources;
  const label = isError ? errorLabel : isRunning ? runningLabel : completeLabel;
  const trailingLabel =
    detailLabel ??
    (hasSources ? `${sourceCount} source${sourceCount === 1 ? "" : "s"}` : "");

  const content = (
    <>
      <Icon className="aui-tool-activity-icon size-4 shrink-0" />
      {isError || label ? (
        <span
          className={cn(
            "min-w-0 truncate",
            isRunning && !isError && "text-loading-glint motion-reduce:animate-none"
          )}
        >
          {label}
        </span>
      ) : (
        <span className="sr-only">Working</span>
      )}
      {isExpandable ? (
        <ChevronRightIcon
          className={cn(
            "aui-tool-activity-chevron size-4 shrink-0 transition-transform",
            open && "rotate-90"
          )}
        />
      ) : null}
      {isExpandable && trailingLabel ? (
        <span className="ml-auto min-w-16 shrink-0 text-right text-xs text-muted-foreground/70">
          {trailingLabel}
        </span>
      ) : null}
    </>
  );

  const row = isExpandable ? (
    <button
      type="button"
      data-slot="tool-activity-row"
      aria-expanded={open}
      disabled={!hasExpandableDetails}
      onClick={onToggleSources}
      className={cn(ROW_CLASS, !actions && "my-2", "min-w-0 flex-1 text-left", isError && "text-destructive")}
    >
      {content}
    </button>
  ) : (
    <div
      data-slot="tool-activity-row"
      className={cn(ROW_CLASS, actions ? "min-w-0 flex-1" : "my-2", isError && "text-destructive")}
    >
      {content}
    </div>
  );

  if (!actions) return row;
  // The buttons live beside the toggle, never inside it, so a click on Keep
  // is never also a click on the row.
  return (
    <div className="my-2 flex w-full items-center gap-1">
      {row}
      <div className="flex shrink-0 items-center gap-1">{actions}</div>
    </div>
  );
};

/**
 * The panel a row opens: the same quiet frame the navigation reason uses,
 * tucked under the line it belongs to.
 * @param props - Component props.
 * @param props.dataSlot - data-slot for tests and styling hooks.
 * @param props.children - The detail.
 * @returns The panel.
 */
export const ToolActivityDetail: FC<{ dataSlot?: string; children: ReactNode }> = ({
  dataSlot,
  children
}) => (
  <div
    data-slot={dataSlot}
    className="mx-1 -mt-1 mb-2 rounded-lg border border-border/80 bg-background px-3 py-2 text-xs leading-relaxed text-muted-foreground"
  >
    {children}
  </div>
);
